import BaseApiClient from '@api-client/BaseApiClient';
import { EventsInterface, ReminderInterface } from '@interfaces/Calendar.interface';
import { ListDataParams, ListDataResponse } from '../types';

export interface StoreEventPayload {
  name: string;
  description: string;
  event_type_id: string;
  start_datetime: string;
  duration_minutes: string;
  max_attendees: string;
  attendees: unknown[];
  task_id?: string | number;
  individual_id?: string | number;
  company_id?: string | number;
  timezone: string;
}

export interface UpdateEventPayload extends StoreEventPayload {
  // Same shape as store — kept as alias for clarity
}

export interface StoreReminderPayload {
  name?: string;
  description?: string;
  event_type_id?: string;
  repeat?: string;
  start_datetime: string;
  snooze?: boolean;
  task_id?: string | number;
  company_id?: string | number;
  /** Assignee user id (same as getTeamMembersUsers member id); sent as multipart `user_id` only. */
  user_id?: string | number;
  documents?: (File | { id: number; file_type: string; file_name: string; file_path: string; file_size: string })[];
  [key: string]: unknown;
}

export interface UpdateReminderPayload extends StoreReminderPayload {
  // Same shape as store — kept as alias for clarity
}

export class ApiCalendar extends BaseApiClient {
  async getEvents(
    listData: ListDataParams,
    searchTerm?: string,
  ): Promise<ListDataResponse<EventsInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/calendar-event', listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async store(body: StoreEventPayload): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.client
        .post('/calendar-event', body)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async update(id: string, body: UpdateEventPayload): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/calendar-event/${id}`, body)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async delete(id: string) {
    return this.client.delete(`/calendar-event/${id}`);
  }

  async storeReminder(body: StoreReminderPayload): Promise<unknown> {
    const formData = new FormData();
    formData.append('name', body.name || '');
    formData.append('description', body.description || '');
    formData.append('event_type_id', body.event_type_id || '');
    formData.append('repeat', body.repeat || '');
    formData.append('start_datetime', body.start_datetime);
    formData.append('snooze', String(body.snooze ?? false));
    if (body.task_id) {
      formData.append('task_id', String(body.task_id));
    }
    if (body.company_id) {
      formData.append('company_id', String(body.company_id));
    }
    if (body.user_id != null && body.user_id !== '') {
      formData.append('user_id', String(body.user_id));
    }
    if (body.process_id != null && body.process_id !== '') {
      formData.append('process_id', String(body.process_id));
    }

    if (body?.documents) {
      body.documents.forEach((document, index) => {
        // If it's a File object (new upload), append it with index
        if (document instanceof File) {
          formData.append(`documents[${index}]`, document);
        }
      });
    }
    return new Promise((resolve, reject) => {
      this.client
        .post('/reminders', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async updateReminder(id: string, body: UpdateReminderPayload): Promise<unknown> {
    const formData = new FormData();
    formData.append('name', body.name || '');
    formData.append('description', body.description || '');
    formData.append('event_type_id', body.event_type_id || '');
    formData.append('repeat', body.repeat || '');
    formData.append('start_datetime', body.start_datetime);
    formData.append('snooze', String(body.snooze ?? false));
    if (body.task_id) {
      formData.append('task_id', String(body.task_id));
    }
    if (body.company_id) {
      formData.append('company_id', String(body.company_id));
    }
    if (body.user_id != null && body.user_id !== '') {
      formData.append('user_id', String(body.user_id));
    }
    if (body.process_id != null && body.process_id !== '') {
      formData.append('process_id', String(body.process_id));
    }

    if (body?.documents) {
      body.documents.forEach((document, index) => {
        // If it's a new file (no id), append it for upload
        if (!('id' in document)) {
          formData.append(`documents[${index}]`, document);
        }
      });
    }
    formData.append('_method', 'PUT');

    return new Promise((resolve, reject) => {
      this.client
        .post(`/reminders/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        .then((res) => {
          if (res.data) resolve(res.data.data);
          else reject('Invalid response');
        })
        .catch((err) => reject(err));
    });
  }

  async getReminder(
    id: string | number | undefined
  ): Promise<ReminderInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/reminders/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async deleteReminder(id: string) {
    return this.client.delete(`/reminders/${id}`);
  }

  async deleteDocument(id: string, documentId: string) {
    return this.client.delete(`/reminders/${id}/documents/${documentId}`);
  }
}

export { calendarQueryKeys } from './query-keys';
export type { CalendarEventsListParams } from './query-keys';
export { useCalendarEventsQuery, useCalendarReminderQuery } from './use-calendar-queries';
export {
  useCalendarDeleteEventMutation,
  useCalendarDeleteReminderDocumentMutation,
  useCalendarDeleteReminderMutation,
  useCalendarStoreEventMutation,
  useCalendarStoreReminderMutation,
  useCalendarUpdateEventMutation,
  useCalendarUpdateReminderMutation
} from './use-calendar-mutations';

